import React, { useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { createVision } from '../../actions/planningAction';
import { FIVE_YEAR_CATEGORIES } from '../UserPortal/fiveYearCategories';
import '../UserPortal/VisionStartFlow.css';
import './PlanningOnboard.css';

const TEXT_LIMIT = 200;
const MIN_CHARS = 10;

const DURATION_THEMES = {
  1: { label: '1 Year' },
  2: { label: '2 Years' },
  3: { label: '3 Years' },
  4: { label: '4 Years' },
  5: { label: 'PROJECT-5' },
};

const ALL_EXAMPLES = [
  'Growing my YouTube channel to 100K subscribers',
  'I am going to make my business a success',
  'Exploring 10 new countries in the next 2 years',
  'Getting into the best shape of my life',
  'Writing and publishing my first book',
  'Building a SaaS that reaches $10K MRR',
  'Running a sub-4-hour marathon',
  'Leaving my job and going full-time on my own thing',
  'Learning a new language fluently',
  'Saving my first $100K',
];

const deriveName = (text) => {
  const t = (text || '').trim();
  if (!t) return 'Untitled Vision';
  return t.length <= 40 ? t : t.slice(0, 40).trim() + '…';
};

export default function PlanningOnboard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [duration, setDuration] = useState(3);
  const [text, setText] = useState('');
  const [category, setCategory] = useState(null);
  const [otherInput, setOtherInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [p5Glitching, setP5Glitching] = useState(false);
  const prevDurationRef = useRef(3);

  const handleDurationChange = (y) => {
    if (y === 5 && prevDurationRef.current !== 5) {
      setP5Glitching(true);
      setTimeout(() => setP5Glitching(false), 520);
    }
    prevDurationRef.current = y;
    setDuration(y);
  };

  const continueDisabled = text.trim().length < MIN_CHARS;

  const handleConfirm = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const { vision } = await dispatch(createVision({
        title: deriveName(text),
        description: text.trim(),
        timeframe: duration,
        category: category || '',
      }));
      navigate(`/dashboard/planning/${vision._id}`);
    } catch {
      setSubmitting(false);
    }
  };

  const examples = useMemo(() => {
    return [...ALL_EXAMPLES].sort(() => Math.random() - 0.5).slice(0, 4);
  }, []);

  const allCategories = Object.keys(FIVE_YEAR_CATEGORIES);
  const theme = DURATION_THEMES[duration];

  return (
    <div className={`vsf-page vsf-theme-y1 vsf-step-${step} ${p5Glitching ? 'vsf-p5-glitching' : ''} ${duration === 5 ? 'vsf-theme-p5' : ''}`}>
      {p5Glitching && (
        <div className="vsf-glitch-fx" aria-hidden="true">
          <div className="vsf-glitch-scan" />
          <div className="vsf-glitch-bars" />
          <div className="vsf-glitch-flash" />
        </div>
      )}

      <div className="vsf-bg" aria-hidden="true">
        <div className="vsf-bg-orb vsf-bg-orb-a" />
        <div className="vsf-bg-orb vsf-bg-orb-b" />
        <div className="vsf-bg-grid" />
      </div>

      {step === 1 && (
        <div className="vsf-stage vsf-stage-duration">
          <p className="vsf-eyebrow">Step 1 of 3 · Horizon</p>
          <h1 className="vsf-heading">How far out are we looking?</h1>
          <p className="vsf-subhead">
            This is the time you are giving yourself to make your vision a reality.
          </p>

          <div className="vsf-duration-display">
            <div className="vsf-duration-ring">
              <span className="vsf-duration-num">{duration === 5 ? 'P5' : duration}</span>
            </div>
            <span className="vsf-duration-label">{theme.label}</span>
          </div>

          <div className="vsf-year-row">
            {[1, 2, 3, 4, 5].map((y) => (
              <button
                key={y}
                type="button"
                className={`vsf-year-pill ${duration === y ? 'active' : ''} ${y === 5 ? 'vsf-year-pill-p5' : ''}`}
                onClick={() => handleDurationChange(y)}
              >
                <span className="vsf-year-pill-num">{y === 5 ? 'P5' : y}</span>
                <span className="vsf-year-pill-text">{y === 5 ? 'Project-5' : y === 1 ? 'year' : 'years'}</span>
              </button>
            ))}
          </div>

          <div className="vsf-actions">
            <button type="button" className="vsf-cta" onClick={() => setStep(2)}>
              Continue
            </button>
            <button type="button" className="vsf-secondary" onClick={() => navigate('/dashboard/planning')}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="vsf-stage">
          <p className="vsf-eyebrow">Step 2 of 3 · Your Vision</p>
          <h1 className="vsf-heading">What's on your mind?</h1>
          <p className="vsf-subhead">
            One thing you're committing to over the next{' '}
            {duration === 5 ? '5 years (Project-5)' : duration === 1 ? '1 year' : `${duration} years`}.
            This becomes your Vision heading.
          </p>

          <div className="vsf-input-wrap">
            <div className="vsf-textarea-chrome">
              <textarea
                className="vsf-textarea"
                value={text}
                onChange={(e) => setText(e.target.value.slice(0, TEXT_LIMIT))}
                placeholder="e.g. I'm going to build a company."
                autoFocus
                rows={4}
              />
              <div className="vsf-textarea-footer">
                <div className="vsf-char-bar">
                  <div
                    className="vsf-char-bar-fill"
                    style={{
                      width: `${Math.min(100, (text.length / TEXT_LIMIT) * 100)}%`,
                      background: text.length > TEXT_LIMIT * 0.9 ? '#e0416d' : '#f17501',
                    }}
                  />
                </div>
                <span className={`vsf-counter-inline ${text.length > TEXT_LIMIT * 0.9 ? 'vsf-counter-warn' : ''}`}>
                  {text.length}/{TEXT_LIMIT}
                </span>
              </div>
            </div>
          </div>

          <div className="vsf-examples">
            <p className="vsf-examples-label">Start with a popular vision</p>
            <div className="vsf-chip-row">
              {examples.map((ex) => (
                <button key={ex} type="button" className="vsf-chip" onClick={() => setText(ex)}>
                  {ex}
                </button>
              ))}
            </div>
          </div>

          <div className="vsf-actions vsf-actions-row">
            <button type="button" className="vsf-secondary" onClick={() => setStep(1)}>Back</button>
            <button type="button" className="vsf-cta" onClick={() => setStep(3)} disabled={continueDisabled}>
              Continue →
            </button>
          </div>
          {continueDisabled && text.length > 0 && (
            <p className="vsf-hint-min">
              {MIN_CHARS - text.trim().length} more character{MIN_CHARS - text.trim().length === 1 ? '' : 's'} to continue
            </p>
          )}
        </div>
      )}

      {step === 3 && (
        <div className="vsf-stage">
          <p className="vsf-eyebrow">Step 3 of 3 · Tag (optional)</p>

          <div className="vsf-quote-card">
            <p className="vsf-quote-text">{text}</p>
          </div>

          <p className="vsf-classify-line">Tag your Vision (optional). You can always change it later.</p>

          <div className="vsf-cat-grid">
            {allCategories.map((cat) => {
              const isActive = category === cat && !otherInput;
              return (
                <button
                  key={cat}
                  type="button"
                  className={`vsf-cat-btn ${isActive ? 'active' : ''}`}
                  onClick={() => {
                    if (isActive) setCategory(null);
                    else { setCategory(cat); setOtherInput(''); }
                  }}
                >
                  {cat}
                </button>
              );
            })}
          </div>

          <div className="vsf-other-row">
            <input
              type="text"
              className="vsf-other-input"
              placeholder="Or type your own (e.g. Travel, Spirituality)"
              value={otherInput}
              maxLength={40}
              onChange={(e) => {
                setOtherInput(e.target.value);
                setCategory(e.target.value.trim() || null);
              }}
            />
          </div>

          <div className="vsf-confirm-row">
            <button type="button" className="vsf-cta" onClick={handleConfirm} disabled={submitting}>
              {submitting ? 'Creating…' : 'Create Vision'}
            </button>
            <button
              type="button"
              className="vsf-secondary"
              onClick={() => { setStep(2); setOtherInput(''); setCategory(null); }}
              disabled={submitting}
            >
              Back
            </button>
          </div>
          <p className="vsf-hint-min">No tag? Skip it. You can add milestones to your Vision next.</p>
        </div>
      )}
    </div>
  );
}
